/**
 * CustomerOverviewTab Component
 * Overview tab content for customer detail page
 */

import {
  Edit,
  User,
  Phone,
  MapPin,
  IndianRupee,
  Users,
  UserCheck,
  ShieldCheck,
  Search,
} from 'lucide-react'
import type { IndividualCustomer } from '@/types/customer'
import { resolveRole } from '@/security/accessContract'
import { useAuth } from '@/contexts/AuthContext'

/** A Quick Action targets one of the customer detail tabs and triggers its create flow. */
export type QuickAction = 'assessments' | 'documents' | 'reminders' | 'notes'

interface CustomerOverviewTabProps {
  customer: IndividualCustomer
  onEdit?: (section: string) => void
  /** Switch to the target tab and open its create flow (assessment / upload / reminder / note). */
  onQuickAction?: (action: QuickAction) => void
  /** Open assignment modal to manage assigned team */
  onManageAssignment?: () => void
  canManageAssignment?: boolean
}

export default function CustomerOverviewTab({
  customer,
  onEdit,
  onQuickAction,
  onManageAssignment,
  canManageAssignment,
}: CustomerOverviewTabProps) {
  const { user, hasPermission } = useAuth()
  const canStartAssessment =
    ['firm_owner', 'firm_admin', 'ca_partner'].includes(
      resolveRole(user?.role).canonicalRole ?? ''
    ) &&
    (hasPermission('txh.assessment.create') || hasPermission('txh.assessment.case.create'))
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' }
    return date.toLocaleDateString('en-IN', options)
  }

  const calculateAge = (dateOfBirth: string) => {
    const birth = new Date(dateOfBirth)
    const today = new Date()
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  const getOccupationLabel = (occupation: string) => {
    const labels: Record<string, string> = {
      salaried: 'Salaried',
      self_employed: 'Self Employed / Professional',
      business: 'Business Owner',
      retired: 'Retired',
      other: 'Other',
    }
    return labels[occupation] || occupation || 'Not provided'
  }

  const getIncomeRangeLabel = (range: string) => {
    const labels: Record<string, string> = {
      '0-5L': 'Below 5 Lakhs',
      '5-10L': '5 - 10 Lakhs',
      '10-25L': '10 - 25 Lakhs',
      '25-50L': '25 - 50 Lakhs',
      '50L+': 'Above 50 Lakhs',
    }
    return labels[range] || range || 'Not provided'
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const Section = ({
    title,
    icon: Icon,
    section,
    children,
  }: {
    title: string
    icon: React.ElementType
    section: string
    children: React.ReactNode
  }) => (
    <div className="card card-elevated p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-text-primary">{title}</h3>
        </div>
        {onEdit && (
          <button
            onClick={() => onEdit(section)}
            className="btn btn-ghost btn-sm flex items-center gap-1"
          >
            <Edit className="w-4 h-4" /> Edit
          </button>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1">
      <span className="text-sm text-text-secondary sm:w-40 flex-shrink-0">{label}</span>
      <span className="text-sm text-text-primary font-medium">{value}</span>
    </div>
  )

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Personal Information */}
      <Section title="Personal Information" icon={User} section="personal">
        <InfoRow
          label="Date of Birth"
          value={
            customer.dateOfBirth
              ? `${formatDate(customer.dateOfBirth)} (${calculateAge(customer.dateOfBirth)} years)`
              : 'Not provided'
          }
        />
        <InfoRow
          label="Gender"
          value={
            customer.gender
              ? customer.gender.charAt(0).toUpperCase() + customer.gender.slice(1)
              : 'Not provided'
          }
        />
        <InfoRow
          label="PAN"
          value={
            <code className="bg-surface-overlay px-2 py-0.5 rounded font-mono">{customer.pan}</code>
          }
        />
        {customer.aadhaar && (
          <InfoRow
            label="Aadhaar"
            value={
              <code className="bg-surface-overlay px-2 py-0.5 rounded font-mono">
                {customer.aadhaar.replace(/(\d{4})/g, '$1 ').trim()}
              </code>
            }
          />
        )}
        <InfoRow
          label="Occupation"
          value={
            <span className="flex items-center gap-2">
              {getOccupationLabel(customer.occupation)}
              {customer.employerName && (
                <span className="text-text-secondary">- {customer.employerName}</span>
              )}
            </span>
          }
        />
        <InfoRow
          label="Annual Income"
          value={`${getIncomeRangeLabel(customer.annualIncomeRange)}`}
        />
      </Section>

      {/* Contact Information */}
      <Section title="Contact Information" icon={Phone} section="contact">
        <InfoRow
          label="Primary Email"
          value={
            <a
              href={`mailto:${customer.emailPrimary}`}
              className="text-primary-600 hover:underline"
            >
              {customer.emailPrimary}
            </a>
          }
        />
        {customer.emailSecondary && (
          <InfoRow
            label="Secondary Email"
            value={
              <a
                href={`mailto:${customer.emailSecondary}`}
                className="text-primary-600 hover:underline"
              >
                {customer.emailSecondary}
              </a>
            }
          />
        )}
        <InfoRow
          label="Primary Mobile"
          value={
            <a href={`tel:${customer.mobilePrimary}`} className="text-primary-600 hover:underline">
              +91 {(customer.mobilePrimary || '').replace(/(\d{5})(\d{5})/, '$1-$2')}
            </a>
          }
        />
        {customer.mobileSecondary && (
          <InfoRow
            label="Secondary Mobile"
            value={`+91 ${customer.mobileSecondary.replace(/(\d{5})(\d{5})/, '$1-$2')}`}
          />
        )}
        {customer.whatsappNumber && (
          <InfoRow
            label="WhatsApp"
            value={`+91 ${customer.whatsappNumber.replace(/(\d{5})(\d{5})/, '$1-$2')}`}
          />
        )}
      </Section>

      {/* Addresses */}
      <Section title="Addresses" icon={MapPin} section="address">
        <div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1">
            Residential Address
          </p>
          <p className="text-sm text-text-primary">{customer.residentialAddress.line1}</p>
          {customer.residentialAddress.line2 && (
            <p className="text-sm text-text-primary">{customer.residentialAddress.line2}</p>
          )}
          <p className="text-sm text-text-primary">
            {customer.residentialAddress.city}, {customer.residentialAddress.state} -{' '}
            {customer.residentialAddress.pincode}
          </p>
        </div>

        {customer.permanentAddress ? (
          <div className="pt-3 border-t border-surface-overlay">
            <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-1">
              Permanent Address
            </p>
            <p className="text-sm text-text-primary">{customer.permanentAddress.line1}</p>
            {customer.permanentAddress.line2 && (
              <p className="text-sm text-text-primary">{customer.permanentAddress.line2}</p>
            )}
            <p className="text-sm text-text-primary">
              {customer.permanentAddress.city}, {customer.permanentAddress.state} -{' '}
              {customer.permanentAddress.pincode}
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-secondary italic pt-2">
            Permanent address same as residential
          </p>
        )}
      </Section>

      {/* Financial Profile */}
      <Section title="Financial Profile" icon={IndianRupee} section="financial">
        <InfoRow
          label="House Property"
          value={
            customer.hasHouseProperty ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded text-xs font-medium">
                Yes
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-xs font-medium">
                No
              </span>
            )
          }
        />
        <InfoRow
          label="Capital Gains"
          value={
            customer.hasCapitalGains ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded text-xs font-medium">
                Yes
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-xs font-medium">
                No
              </span>
            )
          }
        />
        <InfoRow
          label="Business Income"
          value={
            customer.hasBusinessIncome ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded text-xs font-medium">
                Yes
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-xs font-medium">
                No
              </span>
            )
          }
        />
        {customer.previousYearTaxPaid !== undefined && customer.previousYearTaxPaid > 0 && (
          <InfoRow
            label="Previous Year Tax Paid"
            value={formatCurrency(customer.previousYearTaxPaid)}
          />
        )}
      </Section>

      {/* Team & Assignment - Full Width */}
      <div className="lg:col-span-2">
        <div className="card card-elevated p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-600" />
              <h3 className="font-semibold text-text-primary">Team & Assignment</h3>
            </div>
            {canManageAssignment && onManageAssignment && (
              <button
                type="button"
                onClick={onManageAssignment}
                className="btn btn-outline btn-sm flex items-center gap-1.5"
              >
                <UserCheck className="w-4 h-4 text-primary-600" />
                <span>Manage Team</span>
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-3.5 rounded-lg border border-surface-overlay bg-surface-light dark:bg-surface-elevated/40">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-orange-600 dark:text-orange-400 mb-1.5">
                <User className="w-3.5 h-3.5" />
                Preparer / Tech Assistant
              </div>
              <p className="text-sm font-medium text-text-primary">
                {customer.assignedCAName || 'Not Assigned'}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">Documents & Schedule Prep</p>
            </div>

            <div className="p-3.5 rounded-lg border border-surface-overlay bg-surface-light dark:bg-surface-elevated/40">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Reviewer / CA Partner
              </div>
              <p className="text-sm font-medium text-text-primary">
                {customer.reviewerName || 'Not Assigned'}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">Sign-off & Filing Authority</p>
            </div>

            <div className="p-3.5 rounded-lg border border-surface-overlay bg-surface-light dark:bg-surface-elevated/40">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1.5">
                <Search className="w-3.5 h-3.5" />
                Research Assistant
              </div>
              <p className="text-sm font-medium text-text-primary">
                {customer.researcherName || 'Not Assigned'}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">Advisory & Notice Research</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions - Full Width */}
      <div className="lg:col-span-2">
        <div className="card card-elevated p-5">
          <h3 className="font-semibold text-text-primary mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            {canStartAssessment && (
              <button
                type="button"
                onClick={() => onQuickAction?.('assessments')}
                className="btn btn-primary btn-sm"
              >
                Start New Assessment
              </button>
            )}
            {hasPermission('txh.document.upload') && (
              <button
                type="button"
                onClick={() => onQuickAction?.('documents')}
                className="btn btn-outline btn-sm"
              >
                Upload Document
              </button>
            )}
            {hasPermission('txh.reminder.create') && (
              <button
                type="button"
                onClick={() => onQuickAction?.('reminders')}
                className="btn btn-outline btn-sm"
              >
                Set Reminder
              </button>
            )}
            {hasPermission('txh.client.note.create') && (
              <button
                type="button"
                onClick={() => onQuickAction?.('notes')}
                className="btn btn-outline btn-sm"
              >
                Add Note
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
